import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { PartialDeep } from 'type-fest';
import { Toast } from '@components/NotificationsV2/Toast/Toast';
import type { OnNavigateProps } from '@utils/navigation-handler';
import type { ToastData, AddToastProps } from './hotsauceContext';
import { HotsauceContext } from './hotsauceContext';
import styled from '@emotion/styled';
import { spacer } from '@tokens';
import { ZINDEX } from '@utils/constants';
import { DEFAULT_STRINGS } from '@strings';
import type { HotsauceStrings } from '@strings';
import { interpolate as interpolateFallback } from '@utils/interpolate';
import type { InterpolateFn } from '@utils/interpolate';
import { deepmerge } from '@utils/translations-deepmerge';

export const randomId = (): string =>
  Math.random().toString(36).substring(2, 15) +
  Math.random().toString(36).substring(2, 15);

interface PortalProps {
  children: ReactElement;
}

interface HotsauceProviderProps {
  children: ReactElement;
  onNavigate?: (props: OnNavigateProps) => void;
  strings?: HotsauceStrings;
  i18n?: {
    strings: PartialDeep<HotsauceStrings>;
    interpolate?: InterpolateFn;
  };
}

const Portal = ({ children }: PortalProps): JSX.Element => {
  return createPortal(children, document.body);
};

const ToastsContainer = styled.div`
  z-index: ${ZINDEX.TOAST};
  position: fixed;
  bottom: ${spacer.x4};
  right: ${spacer.x4};
  display: flex;
  flex-direction: column;
  gap: ${spacer.x4};
`;

const defaultI18NProps = {
  strings: DEFAULT_STRINGS,
  interpolate: interpolateFallback,
};

export const HotsauceProvider = ({
  children,
  onNavigate,
  i18n = defaultI18NProps,
}: HotsauceProviderProps): JSX.Element => {
  const [toasts, setToasts] = useState<Array<ToastData>>([]);
  const { strings = {} } = i18n;
  const { interpolate = interpolateFallback } = i18n;

  const stringsWithFallbacks = useMemo(
    () => deepmerge(DEFAULT_STRINGS, strings) as HotsauceStrings,
    [strings],
  );

  const addToast = ({ text, kind }: AddToastProps): string => {
    const newToast: ToastData = {
      text,
      kind,
      id: randomId(),
    };

    setToasts((oldToasts) => [...oldToasts, newToast]);
    return newToast.id;
  };

  const removeToast = (toastId: string): void => {
    setToasts((oldToasts) => oldToasts.filter(({ id }) => id !== toastId));
  };

  return (
    <HotsauceContext.Provider
      value={{
        addToast,
        removeToast,
        onNavigate,
        strings: stringsWithFallbacks,
        interpolate,
      }}
    >
      {children}
      <Portal>
        {toasts.length > 0 && (
          <ToastsContainer>
            {toasts.map(({ text, kind, id }) => (
              <Toast kind={kind} key={id}>
                {text}
              </Toast>
            ))}
          </ToastsContainer>
        )}
      </Portal>
    </HotsauceContext.Provider>
  );
};
